import React, { useMemo } from 'react';
import type { WbsFlatItem } from '../../types';
import type { WeekFlag } from '../../types';
import { calcSPI } from '../../../../lib/wbs/progressCalc';
import { buildRollupRows, computeOverallProgress } from 'src/features/wbs/lib/progressRollup';
import { statusFromRow } from '../common/statusFromRow';

import AsOfSelector, { type WbsEvViewMode } from '../overview/AsOfSelector';
import OverviewToolbarV2 from './OverviewToolbarV2';
import StatusDistributionDonutCardV2, { type StatusDist5, type WbsStatus5 } from './StatusDistributionDonutCardV2';
import ProgressHealthChartCard from './ProgressHealthChartCard';
import ExecutionSummaryChartCard, { type WorkPackage, type Company } from './ExecutionSummaryChartCard';

export type FilterPreset = 'PENDING' | 'APPROVED' | 'REJECTED' | 'DELAY' | 'DUE_SOON' | 'NOT_STARTED';
export type ViewAs = 'DATE' | 'WEEK';

type Props = {
    canEdit?: boolean;
    canApprove?: boolean;
    rows: WbsFlatItem[];
    flags: Record<string, WeekFlag>;
    onOpenApprovalQueue: () => void;
    onFilterPreset?: (preset: FilterPreset) => void;
    onRefresh?: () => void;
    refreshing?: boolean;
    lastRefreshedAt?: Date | null;
    viewAs: ViewAs;
    baseDate: Date;
    onChangeViewAs: (v: ViewAs) => void;
    onChangeBaseDate: (d: Date) => void;
    weekOfISO?: string;
    anchorDowLabel?: string;
    weeklyProgressComparison?: any | null;
    companySummaryWorkPackages?: WorkPackage[];
    companySummaryCompanies?: Company[];
    onSelectCompanyFromSummary?: (companyId: string | null) => void;
    evViewMode: WbsEvViewMode;
    onChangeEvViewMode: (v: WbsEvViewMode) => void;
    liveAvailable?: boolean;
};

function resolveStatus5ByListStatus(row: WbsFlatItem, pv: number | null, baseDate: Date): WbsStatus5 {
    const ev = typeof (row as any).earnedValue === 'number' ? (row as any).earnedValue : 0;
    if (ev >= 100) return 'COMPLETED';

    const kind = statusFromRow(row, pv, baseDate);

    if (kind === 'PLANNED') return 'NOT_STARTED';
    if (kind === 'IMPENDING') return 'NOT_STARTED';
    if (kind === 'ON_TRACK') return 'ON_TRACK';
    if (kind === 'RISK') return 'AT_RISK';
    if (kind === 'DELAY') return 'DELAYED';

    return 'ON_TRACK';
}

export default function OverviewV2({
    rows,
    flags,
    canEdit,
    canApprove,
    onOpenApprovalQueue,
    onFilterPreset,
    onRefresh,
    refreshing,
    lastRefreshedAt,
    viewAs,
    baseDate,
    onChangeViewAs,
    onChangeBaseDate,
    weekOfISO,
    anchorDowLabel,
    weeklyProgressComparison: _weeklyProgressComparison,
    companySummaryWorkPackages,
    companySummaryCompanies,
    onSelectCompanyFromSummary,
    evViewMode,
    onChangeEvViewMode,
    liveAvailable,
}: Props) {
    const derived = useMemo(() => {
        const rolledRows = buildRollupRows(rows);
        const overall = computeOverallProgress(rows);

        const submitCounts: Record<'PENDING' | 'APPROVED' | 'REJECTED', number> = {
            PENDING: 0,
            APPROVED: 0,
            REJECTED: 0,
        };

        for (const f of Object.values(flags ?? {})) {
            const s =
                (f as any)?.previousWeekStatus === 'PENDING'
                    ? 'PENDING'
                    : ((f as any)?.statusForWeek as 'PENDING' | 'APPROVED' | 'REJECTED' | undefined);
            if (!s) continue;
            submitCounts[s] = (submitCounts[s] ?? 0) + 1;
        }

        const parentSet = new Set<string>();
        for (const r of rolledRows) {
            if (r.parentId) parentSet.add(String(r.parentId));
        }

        const distW: Record<WbsStatus5, number> = {
            NOT_STARTED: 0,
            ON_TRACK: 0,
            AT_RISK: 0,
            DELAYED: 0,
            COMPLETED: 0,
        };

        let leafTotalW = 0;

        for (const r of rolledRows) {
            const w = (r as any).actualWeight ?? (r as any).weight ?? 0;
            if (!Number.isFinite(w) || w <= 0) continue;

            const isLeaf = !parentSet.has(String(r.id));
            if (!isLeaf) continue;

            const pv = r.plannedValue ?? null;
            leafTotalW += w;

            const s5 = resolveStatus5ByListStatus(r, pv, baseDate);
            distW[s5] += w;
        }

        const distBase = leafTotalW || 1;
        const statusDist5: StatusDist5 = {
            NOT_STARTED: (distW.NOT_STARTED / distBase) * 100,
            ON_TRACK: (distW.ON_TRACK / distBase) * 100,
            AT_RISK: (distW.AT_RISK / distBase) * 100,
            DELAYED: (distW.DELAYED / distBase) * 100,
            COMPLETED: (distW.COMPLETED / distBase) * 100,
        };

        return {
            submitCounts,
            pvPctWeighted: overall.plannedPct,
            evPctWeighted: overall.actualPct,
            spi: calcSPI(overall.actualPct, overall.plannedPct),
            statusDist5,
        };
    }, [baseDate, flags, rows]);

    return (
        <section className="mb-4 w-full rounded-2xl border bg-white p-4">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-gray-900">WBS Overview</h3>
                    <AsOfSelector
                        viewAs={viewAs}
                        baseDate={baseDate}
                        onChangeViewAs={onChangeViewAs}
                        onChangeBaseDate={onChangeBaseDate}
                        weekOfISO={weekOfISO}
                        anchorDowLabel={anchorDowLabel}
                        evViewMode={evViewMode}
                        onChangeEvViewMode={onChangeEvViewMode}
                        liveAvailable={liveAvailable}
                    />
                </div>

                <OverviewToolbarV2
                    pvPctWeighted={derived.pvPctWeighted}
                    evPctWeighted={derived.evPctWeighted}
                    spi={derived.spi}
                    pendingCount={derived.submitCounts.PENDING}
                    canEdit={canEdit}
                    canApprove={canApprove}
                    onOpenApprovalQueue={onOpenApprovalQueue}
                    onRefresh={onRefresh}
                    refreshing={refreshing}
                    lastRefreshedAt={lastRefreshedAt}
                />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                <div className="min-w-0">
                    <StatusDistributionDonutCardV2
                        dist={derived.statusDist5}
                        onClickStatus={(s) => {
                            if (!onFilterPreset) return;
                            if (s === 'DELAYED') onFilterPreset('DELAY');
                            if (s === 'NOT_STARTED') onFilterPreset('NOT_STARTED');
                        }}
                    />
                </div>

                <div className="min-w-0">
                    <ProgressHealthChartCard
                        spi={derived.spi}
                        evPct={derived.evPctWeighted}
                        pvPct={derived.pvPctWeighted}
                    />
                </div>

                <div className="min-w-0 lg:col-span-2 2xl:col-span-1">
                    <ExecutionSummaryChartCard
                        workPackages={companySummaryWorkPackages ?? []}
                        companies={companySummaryCompanies ?? []}
                        onSelectCompany={onSelectCompanyFromSummary}
                    />
                </div>
            </div>
        </section>
    );
}
